//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { apis } from 'utils/lib/apis';
import { has } from "lodash";
//  -- Actions ------------------------
import { logToSentry } from "utils/networkErrorUtils";
import * as WidgetsActions from './actions';

//  ----------------------------------------------------------------------------
//  Thunks
//  ----------------------------------------------------------------------------

const handleError = (error, operation, defaultMessage) => {
    if (error && error.message !== "Network Error") {
        logToSentry({
            message: `${operation} widgets`,
            error: typeof error === 'string' ? { message: error } : error,
            module: 'WIDGETS',
            operation: operation.toUpperCase()
        });
    }
};

const validateResponse = (response, operation, defaultMessage) => {
    if (!response || response.error || response.success === false || response.status_code >= 400) {
        handleError(response || { message: defaultMessage }, operation, defaultMessage);
        return null;
    }
    return response.data || response;
};

//  -- Create Widgets --------------------
export const handleCreateWidgets = (data) => async (dispatch) => {
    if (!data) {
        handleError('Widget data is required', 'CREATE_WORKSHEET', 'Widget data is required');
        return null;
    }

    try {
        const response = await apis.post('widgetsRDS', `/create`, { body: data });
        const widgetData = validateResponse(response, 'CREATE_WORKSHEET', 'Failed to create widget');
        if (!widgetData) return null;

        if (data.id) {
            await dispatch(WidgetsActions.updateWidgets(widgetData));
        } else {
            await dispatch(WidgetsActions.addWidgets(widgetData));
        }
        return widgetData;
    } catch (error) {
        handleError(error, 'CREATE_WORKSHEET', 'An unexpected error occurred while creating widget');
        return null;
    }
};

//  -- List Widgets ----------------
export const handleListWidgets = (userId) => async (dispatch) => {
    if (!userId) {
        handleError('User ID is required', 'listing', 'User ID is required to fetch widgets');
        return null;
    }

    try {
        const response = await apis.get('widgetsRDS', `/list/${userId}`);
        const widgetsData = validateResponse(response, 'LIST_WORKSHEET', 'Failed to retrieve widgets');
        if (!widgetsData) return null;

        const widgetsList = Array.isArray(widgetsData) ? widgetsData : (widgetsData.rows || []);
        await dispatch(WidgetsActions.listWidgets(widgetsList));
        return widgetsList;
    } catch (error) {
        handleError(error, 'LIST_WORKSHEET', 'An unexpected error occurred while fetching widgets');
        return null;
    }
};

//  -- Delete Widgets ----------
export const handleDeleteWidgets = (id) => async (dispatch) => {
    if (!id) {
        handleError('Widget ID is required', 'DELETE_WORKSHEET', 'Widget ID is required for deletion');
        return null;
    }

    try {
        const response = await apis.del('widgetsRDS', `/delete/${id}`);
        const deleteWidgets = validateResponse(response, 'DELETE_WORKSHEET', 'Failed to delete widget');
        if (!deleteWidgets) return null;

        if (has(deleteWidgets, "id")) {
            await dispatch(WidgetsActions.deleteWidgets(deleteWidgets));
        }
        return deleteWidgets;
    } catch (error) {
        handleError(error, 'DELETE_WORKSHEET', 'An unexpected error occurred while deleting widget');
        return null;
    }
};
